// Import react elements
import { useContext, useMemo, useEffect, useRef, useState } from "react";

// Import api calls
import { 
    addNewConnection, 
    updateConnection, 
    checkConnectionAlias,
    updateConnectionAlias,
    updateConnectionSettings,
    getPodDesigns,
    addNewConnectionPage,
    deleteConnectionPage,
    updateConnectionPage
} from "api/APIStrichCalls"; 

// Import context function
import { UserContext } from "global/context/UserContext";

// Import components
import CreateButton from "global/components/CreateButton";
import ToggleSwitch from "global/components/ToggleSwitch";
import  RenderConnectionSettings  from "./ConnectionSettings";
import  RenderConnectionShippingMethods  from "./ConnectionShippingMethods";
import  RenderConnectionSocials  from "./ConnectionSocials";
import  RenderConnectionPages  from "./ConnectionPages";
import  RenderConnectionEmailTemplates  from "./ConnectionEmailTemplates";
import OpenStoreButton from "../../../global/components/OpenStoreButton";
import { LoadingSpinner } from "global/components/loading_ui/LoadingSpinnerAnimation";
import InlineStorePreviewSettings from "./InlineStorePreviewSettings";
import { InfoButton } from "global/components/InfoButton";
import { CloseIcon } from "global/strich_icons/StrichIcons";

// Import global hooks
import useFormHandler from "global/hooks/useFormHandler";
import { useAppConfig } from "global/hooks/useAppConfig";
import { useToast } from "global/hooks/useToast";
import useIsMobile from "global/hooks/useIsMobile";

// Import utility functions
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";
import { S2B } from "global/utilities/BooleanConverters";
import { IsEmailValue } from "global/utilities/CheckData";






// This checks whether a connection setting has changed
const HasSettingChanged = (originalValue, currentValue) => {

    // Convert the original value safely
    const safeOriginalValue = originalValue ?? "";

    // Convert the current value safely
    const safeCurrentValue = currentValue ?? "";

    // Return whether the values are different
    return String(safeOriginalValue) !== String(safeCurrentValue);

};



// This gets only edited settings
const GetEditedSettings = (currentSettings = [], originalSettings = []) => {

    // Return only settings where the value has changed
    return currentSettings.filter((setting) => {

        // Find the original matching setting
        const originalSetting = originalSettings.find((original) => {

            // Match by type and code
            return original.type === setting.type && original.code === setting.code;

        });

        // Return true if the setting is new or changed
        return !originalSetting || HasSettingChanged(originalSetting.value, setting.value);

    });

};



// This removes separately-saved data before updating the connection
const RemoveSettingsFromPayload = (payload) => {

    // Copy the payload
    const cleanedPayload = { ...payload };

    // Remove settings because they are saved separately
    delete cleanedPayload.settings;

    // Remove pages because they are saved through their own endpoints
    delete cleanedPayload.pages;

    // Return the cleaned payload
    return cleanedPayload;

};



// This cleans shipping methods before save so we do not send legacy pricing fields back to the API.
const CleanShippingMethodsForSave = (shippingMethods = []) => {

    // Clean each shipping method
    return shippingMethods.map((shippingMethod) => {

        // Create a clean pricing object
        const cleanedPricing = {};

        // Go through each currency pricing row
        Object.entries(shippingMethod.pricing || {}).forEach(([currencyCode, pricing]) => {

            // Keep only the fields still used by the API
            cleanedPricing[currencyCode] = {
                price_inc_tax: pricing?.price_inc_tax ?? "",
                free_at: pricing?.free_at ?? "",
            };

        });

        // Return the cleaned shipping method
        return {
            ...shippingMethod,
            pricing: cleanedPricing,
        };

    });

};






const ConnectionDetailsPopUp = ({
    isOpen,
    onClose,
    token,
    connectionDetails,
    addingNewConnection,
    setAddingNewConnection,
    fetchConnections,
    handleDeleteConnection,
    loadingConnection,
}) => {

    //console.log("connectionDetails: ", connectionDetails);


    // Get canonical products and connections from global user data
    const { 
        refreshConnectionsData,
        userData,
        countries,
    } = useContext(UserContext);

    // Set isMobile from use hook 
    const isMobile = useIsMobile();

    // Define toast options
    const { toastSuccess, toastError } = useToast();

    // Get the current environment configuration
    const appConfig = useAppConfig();

    // Set state to check if form is partly edited
    const [formIsPartlyEdited, setFormIsPartlyEdited] = useState(false);

    // Set state for alias exists message
    const [aliasExists, setAliasExists] = useState("");


    // This stores whether the alias has been edited
    const [aliasHasBeenEdited, setAliasHasBeenEdited] = useState(false);

    // Track whether the copy action succeeded
    const [copied, setCopied] = useState(false);

    // Track whether the copy action failed
    const [copyError, setCopyError] = useState("");

    // Keep available account POD templates ready for the inline preview template dropdown.
    const [availablePodTemplates, setAvailablePodTemplates] = useState([]);

    // Recompute memoized form values when the selected connection payload changes.
    const connectionDetailsSyncKey = useMemo(() => JSON.stringify(connectionDetails || null), [connectionDetails]);

    // State for selected tab index
    const [activeTab, setActiveTab] = useState(0);

    // Keep a reference to the popup body which owns the vertical scrollbar
    const popupContentRef = useRef(null);

    // Return the popup body to the very top whenever the selected tab changes
    useEffect(() => {

        // Stop if the popup body has not rendered yet
        if (!popupContentRef.current) {
            return;
        }

        // Start the newly selected tab at the absolute top
        popupContentRef.current.scrollTop = 0;

    }, [activeTab]);



    // Memoize the initial form values to avoid recalculating unless connectionDetails change
    const initialFormValues = useMemo(() => {
        
        if (connectionDetails) {

            // Return the initial form values
            return {

                // Spread all existing connection details into the form state
                ...connectionDetails,
                active: S2B(connectionDetails.active),
                conntype: addingNewConnection ? "STRICH" : connectionDetails.conntype,

            };

        }

        // Check if we're adding a new connection
        if (addingNewConnection) {
                
            // Return the initial form values to include a hardcoded 'type' for now
            return {
              
                conntype: "STRICH",

            };

        }
    
    // Dependency array to recompute initialFormValues only when connectionDetails change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addingNewConnection, connectionDetailsSyncKey]);




    useEffect(() => {

        // Work out the active connection type from either the saved connection or the draft initial values.
        const activeConnectionType = String(connectionDetails?.conntype || initialFormValues?.conntype || "").trim().toUpperCase();

        // Only load POD templates while this popup is open for a STRICH connection and we have auth available.
        if (!isOpen || activeConnectionType !== "STRICH" || !token) {
            return;
        }

        // Track cleanup so async template results do not update state after the popup closes.
        let cancelled = false;

        // Load account-level POD templates for the inline preview settings template dropdown.
        const fetchPodTemplates = async () => {
            try {

                // Ask the API for POD designs filtered to template entries only.
                const response = await getPodDesigns(token, "template");

                // Save the returned templates only if this effect is still current.
                if (!cancelled) {
                    setAvailablePodTemplates(Array.isArray(response?.results) ? response.results : []);
                }
            } catch (error) {

                // Fail quietly here and just show an empty template list rather than breaking the popup.
                if (!cancelled) {
                    setAvailablePodTemplates([]);
                }
            }
        };

        // Start loading templates as soon as the popup is ready for them.
        fetchPodTemplates();

        return () => {

            // Mark this effect as cleaned up so late async responses are ignored.
            cancelled = true;
        };

    }, [connectionDetails?.conntype, initialFormValues?.conntype, isOpen, token]);



    // Handle save confirmation if alias has been edited button click
    const handleSave = async () => {

        if (aliasHasBeenEdited && !addingNewConnection) {

             // Return a promise so the submit handler can await the user's choice
            return new Promise((resolve) => {

                // Give dialog - are you sure you want to change the alias
                DisplayConfirmationDialog(

                    // Dialog message
                    "<h3>Are you sure you want to change the store URL?</h3></br/><span class='warning-message'>WARNING: Changing your store URL will make your current store link stop working. Any saved, shared, or bookmarked links customers already use will no longer work. Customers will need to use the new URL instead.</span>",

                    // Confirm button title
                    "Yes",

                    // Cancel button title
                    "Cancel",

                    // Returned response from the dialog box
                    function (is_confirmed) {

                        // Resolve true when confirmed, otherwise false
                        resolve(Boolean(is_confirmed));

                    }

                );

            });

        } else {

            return true;

        }

    }



    // This saves connection details and settings separately
    const handleConnectionSave = async (preparedData) => {

        // Get edited settings
        const editedSettings = GetEditedSettings(
            formValues.settings,
            connectionDetails?.settings,
        );

        // Remove settings from the connection payload
        const connectionPayload = RemoveSettingsFromPayload(preparedData);

        // Remove old shipping tax rates before saving
        if (Array.isArray(connectionPayload.shipping_methods)) {
            connectionPayload.shipping_methods = CleanShippingMethodsForSave(connectionPayload.shipping_methods);
        }

        // Check whether any main connection fields changed.
        const hasConnectionChanges = Object.keys(connectionPayload).some((key) => {

            // Ignore always-kept id fields
            return !["id", "connection_id"].includes(key);

        });

        // Check whether settings have changed
        const hasSettingChanges = editedSettings.length > 0;

        // Check whether any of the pages have been edited (removed, added or amended)
        const havePagesBeenEdited = preparedData?.pages?.find((page) => 
            (page.change_type === "created")
            || (page.change_type === "deleted")
            || (page.change_type === "updated")
        );

        
        // Stop here when nothing changed.
        if (!hasConnectionChanges && !hasSettingChanges && !addingNewConnection) {

            // Exit early
            return;

        }

        // Save the main connection only if needed
        let connectionResponse = null;

        // Check if adding a new connection
        if (addingNewConnection) {

            // Add the new connection
            connectionResponse = await addNewConnection(token, connectionPayload);

        } else if (hasConnectionChanges) {

            // Update the existing connection
            connectionResponse = await updateConnection(token, connectionPayload);

        }

        // Save edited settings separately
        if (!addingNewConnection && hasSettingChanges) {

            // Save each changed setting one by one.
            for (const setting of editedSettings) {

                // Build the setting payload
                const settingPayload = {

                    // Keep the connection id
                    connection_id: connectionDetails.id,

                    // Keep the setting type
                    type: setting.type,

                    // Keep the setting code
                    code: setting.code,

                    // Keep the setting name
                    name: setting.name,

                    // Keep the setting description
                    description: setting.description,

                    // Keep the edited value
                    value: setting.value,

                };

                // Update the setting
                const settingResponse = await updateConnectionSettings(token, settingPayload);

                // Throw if the setting failed
                if (!settingResponse.success) {

                    // Show error toast
                    toastError({
                        title: "Error",
                        message: `Could not update setting: ${settingResponse.error_message || "Please try again later."}`,
                        autoCloseMs: 6000,
                    });

                    // Exit early
                    return;

                }

            }

        }


        // Track whether any individual page operation fails
        let pageSaveFailed = false;

        if (havePagesBeenEdited) {

            // Get all the pages that have been deleted
            const deletedPages = preparedData?.pages?.filter((page) => page.change_type === "deleted") || [];

            // Get all the pages that have been added
            const addedPages = preparedData?.pages?.filter((page) => page.change_type === "created") || [];

            // Get all the pages that have been edited
            const editedPages = preparedData?.pages?.filter((page) => page.change_type === "updated" && page.source === "user") || [];



            // Delete pages
            for (const page of deletedPages) {

                // Create the payload using the real API id when this page was created during this popup session
                const payload = {
                    ...page,
                    id: page.api_id || page.id,
                    connection_id: connectionDetails.id,
                };

                // Make the call
                const response = await deleteConnectionPage(token, connectionDetails.id, payload);

                // Keep failed deletes queued for another save
                if (!response.success) {

                    pageSaveFailed = true;

                    toastError({
                        title: "Error",
                        message: `Could not delete ${page.name} page: ${response.error_message || "Please try again later."}`,
                        autoCloseMs: 6000,
                    });

                    continue;

                }

                // Successful deletes can now be removed from the local draft completely
                setFormValues((previousValues) => ({
                    ...previousValues,
                    pages: previousValues.pages.filter((existingPage) => existingPage.id !== page.id),
                }));

            }



            // Add new pages
            for (const page of addedPages) {

                // Create the payload
                const payload = {
                    ...page,
                    connection_id: connectionDetails.id,
                    id: null,
                };

                // Make the call
                const response = await addNewConnectionPage(token, connectionDetails.id, payload);

                // Keep failed pages exactly as the user created them
                if (!response.success) {

                    pageSaveFailed = true;

                    toastError({
                        title: "Error",
                        message: `Could not add ${page.name} page: ${response.error_message || "Please try again later."}`,
                        autoCloseMs: 6000,
                    });

                    continue;

                }

                // Mark the successful draft as saved while keeping its temporary UI id stable
                setFormValues((previousValues) => ({
                    ...previousValues,
                    pages: previousValues.pages.map((existingPage) => {

                        // Return unrelated pages unchanged
                        if (existingPage.id !== page.id) { return existingPage; }

                        // Keep the local id so the currently selected page does not disappear,
                        // but remember the real API id for any later update or delete
                        return {
                            ...existingPage,
                            api_id: response?.results?.id || existingPage.api_id,
                            slug: response?.results?.slug || existingPage.slug,
                            ordering: response?.results?.ordering || existingPage.ordering,
                            change_type: "none",
                        };

                    }),
                }));

            }



            // Update pages
            for (const page of editedPages) {

                // Create the payload using the real API id where required
                const payload = {
                    ...page,
                    id: page.api_id || page.id,
                    connection_id: connectionDetails.id,
                };

                // Make the call
                const response = await updateConnectionPage(token, connectionDetails.id, payload);

                // Keep failed edits available for another save
                if (!response.success) {

                    pageSaveFailed = true;

                    toastError({
                        title: "Error",
                        message: `Could not edit ${page.name} page: ${response.error_message || "Please try again later."}`,
                        autoCloseMs: 6000,
                    });

                    continue;

                }

                // Successful edits no longer need saving
                setFormValues((previousValues) => ({
                    ...previousValues,
                    pages: previousValues.pages.map((existingPage) => {

                        // Mark only the successfully updated page as clean
                        if (existingPage.id === page.id) {

                            return {
                                ...existingPage,
                                change_type: "none",
                            };

                        }

                        return existingPage;

                    }),
                }));

            }

            // If anything page-related failed, leave the popup and drafts exactly where they are
            if (pageSaveFailed) {

                return {
                    success: false,
                };

            }

        }


        if (connectionResponse && connectionResponse.success) {

             // Clear the adding new state
            setAddingNewConnection(false);
            
            // Clear the alias message
            setAliasExists("");

            if (addingNewConnection) {
        
                // Show success message
                toastSuccess({title: "Success", message: "Store has been added.",});

            } else {

                // Check if the alias has been editted (only when not adding a new connection)
                if (connectionDetails?.alias !== formValues.alias) {

                    // Persist alias changes through the alias endpoint as well because store URLs are managed separately.
                    updateAlias(token, connectionDetails.id, formValues.alias);

                };

            }

        } else if (connectionResponse && !connectionResponse.success) {

            // Set error message
            const errorMsg = `Could not ${addingNewConnection ? 'add' : 'update'} store: ${connectionResponse.error_message ? connectionResponse.error_message : "Please try again later."}`

            // Toast error message
            toastError({ title: "Error", message: errorMsg, autoCloseMs: 6000 });

        }
        
        // Clear edited flag
        setFormIsPartlyEdited(false);

        // Refresh connection data
        refreshConnectionsData();

        // Refresh the connections list
        fetchConnections(connectionDetails?.id || connectionResponse?.results?.[0]?.id || "");

    };



    // Extract form-related state and functions from the `useFormHandler` custom hook
    const {

        formValues,     // Stores the current values of form inputs
        setFormValues,  // Direct state setter for non-standard inputs like the colour picker
        handleChange,   // Function to update form values when inputs change
        handleSubmit,   // Function to handle form submission logic
        resetForm,      // Function to reset data on the form
        isFormPartlyEdited, // Check to see if form has been partially edited
        fieldErrors,    // Object storing validation errors for individual fields
        formError,      // General error message (e.g. API errors)
        loading,        // Boolean indicating whether the form is submitting
        
    } = useFormHandler({

        // Default values for the form fields
        initialValues: initialFormValues,

        // Only submit changed fields
        keepEditedDataOnly: true,

        // Always include these fields
        fieldsToAlwaysKeep: [
            "id",
            "connection_id",
            "conntype",
        ],

        // Always remove these fields
        fieldsToAlwaysRemove: [
            "settings",
        ],

        // Enable syncing - because we're using memo to remeber the initial form values before the user edits them
        syncInitialValues: true,

        // Send confirmation function when saving the form
        confirmationFunction: handleSave,

        // API function to be called on form submission based on adding a new or updating an existing one
        //apiCall: addingNewConnection ? addNewConnection : updateConnection,

        submitFunction: handleConnectionSave,

        ignoreEditedCheckFields: ["pages"],

        // We want to convert boolean to string
        convertBooleans: true,

        // We want to convert number strings to numbers
        convertNumbers: true,

        // Validation function to check the form fields before submission
        validate: (values) => {

            // Set errors to empty
            const errors = {};

            // Validation rules for form fields

            // Check if name is missing OR if it's a string that is empty or just whitespace
            if (
                values.connectionname === undefined || // name is missing
                values.connectionname === null || // name is explicitly null
                (typeof values.connectionname === 'string' && !values.connectionname.trim()) // name is an empty string or just spaces
            ) {
                // Set the validation error message
                errors.connectionname = "*Store name is required";
                
            }

            // Check if alias is missing OR if it's a string that is empty or just whitespace
            if (
                values.alias === undefined || // alias is missing
                values.alias === null || // alias is explicitly null
                (typeof values.alias === 'string' && !values.alias.trim()) // alias is an empty string or just spaces
            ) {
                // Set the validation error message
                errors.alias = "*Store URL is required";
            }

            // Validate email address input
            const store_email = values.settings?.find((setting)  => setting.code === "store_email")?.value;
            const storeEmailIsValidEmail = IsEmailValue(store_email);
            if (
                store_email &&
                store_email !== "" &&
                !storeEmailIsValidEmail
            )
                // Set the validation error message
                errors.store_email = "*Store email is not a valid email address";

            // Return validation errors (if any)
            return errors;

        },

    });



    // Check if the form is partly edited before updating the values
    useEffect(() => {

        // Check if the form is partly edited
        setFormIsPartlyEdited(isFormPartlyEdited());

        // Re-evaluate when form values change
    }, [formValues, isFormPartlyEdited]);



    // Async function to update the alias
    const updateAlias = async (token, connectionID, newAlias) => {

        // Send the alias update to the API.
        const updateAliasResponse = await updateConnectionAlias(token, connectionID, newAlias);

        // Check if it fails
        if (!updateAliasResponse.success) {

            //console.log("Error updating alias: ", updateAliasResponse.error_message);

        }

    };



    // Check if the alias has been edited
    useEffect(() => {

        // Check if the alias matches the initial formValues
        if (formValues?.alias === initialFormValues?.alias) {

            // It does so clear the flag
            setAliasHasBeenEdited(false);

        } else {

            // It doesn't so set the flag
            setAliasHasBeenEdited(true);

        }
        

        // Re-evaluate when form values change
    }, [formValues?.alias, initialFormValues?.alias]);

    // Check whether any store pages still contain unsaved changes
    const pagesNeedSaving = formValues?.pages?.some((page) => page.change_type !== "none");

    // Treat normal form changes and page changes as one unsaved state
    const hasUnsavedChanges = formIsPartlyEdited || pagesNeedSaving;

    

    // This handles closing the popup and warns before discarding any unsaved changes.
    const handleCloseButton = (dialogMessage) => {

        // Check whether anything in the store or its pages remains unsaved
        if (hasUnsavedChanges) {

            // It has

            // Display a confrimation dialog
            DisplayConfirmationDialog(

                // Dialog message
                dialogMessage,

                // Confirm button title
                "Yes, discard changes",

                // Cancel button title
                "No, keep editing",

                // Returned response from the dialog box
                function (is_confirmed) {

                    // Check if the response is TRUE
                    if (is_confirmed) {

                        // It is

                        // Reset the form values
                        resetForm(initialFormValues);

                        // Clear the alias message
                        setAliasExists("");

                        // Close the pop up
                        onClose();

                    }

                }

            );

        } else {

            // Clear the alias message
            setAliasExists("");

            // Reset the form values
            resetForm(initialFormValues);

            // Close the pop up
            onClose();

        }

    };

   

    // This inserts or replaces one draft setting row inside formValues.settings while preserving saved metadata when available.
    const buildUpdatedSettings = (settingsArray = [], {
        type,
        code,
        name,
        description,
        value,
    }) => {

        // Normalise the current settings array so we can safely search and update it.
        const existingSettings = Array.isArray(settingsArray) ? settingsArray : [];

        // Prefer the draft copy of the setting when it already exists in the form state.
        const existingDraftSettingByCode = existingSettings.find((setting) => setting.code === code);

        // Fall back to the saved connection setting if the draft version is not present yet.
        const existingSavedSettingByCode = connectionDetails?.settings?.find((setting) => setting.code === code);

        // Reuse whatever metadata we already know about this setting before applying the new value.
        const fallbackSetting =
            existingDraftSettingByCode
            || existingSavedSettingByCode
            || {};

        // Build the next canonical setting object so both text inputs and preview settings update the same shape.
        const nextSetting = {
            ...fallbackSetting,
            type: fallbackSetting.type || type || "style",
            code,
            name: fallbackSetting.name || name || code,
            description: fallbackSetting.description || description || "",
            value,
        };

        // Check whether this setting already exists in the current draft state.
        const settingExists = existingSettings.some((setting) => setting.code === code);

        // Replace the existing setting when present, otherwise append a brand new row.
        return settingExists
            ? existingSettings.map((setting) => {
                if (setting.code !== code) {
                    return setting;
                }

                return {
                    ...setting,
                    ...nextSetting,
                };
            })
            : [...existingSettings, nextSetting];
    };



    // This applies one setting update to the form state so unsaved preview/settings edits stay in sync across the popup.
    const upsertDraftSetting = (settingUpdate) => {

        setFormValues((previousValues) => ({
            ...previousValues,
            settings: buildUpdatedSettings(previousValues?.settings, settingUpdate),
        }));

    };



    // This updates one draft setting from a normal input change event.
    const handleSettingsChange = (type, code, name = "", description = "" ) => (e) => {

        // Read the raw input value from the event
        const rawValue = e.target.type === "checkbox" ? e.target.checked : e.target.value;

        // Save the new value into the draft settings array using the shared upsert helper.
        upsertDraftSetting({
            type,
            code,
            name,
            description,
            value: rawValue,
        });

    };



    // This updates one draft setting from controls that send values directly.
    const handleSettingValueChange = (type, code, name = "", description = "") => (nextValue) => {

        // Save non-event-driven setting changes such as custom pickers or preview controls into the same draft settings array.
        upsertDraftSetting({
            type,
            code,
            name,
            description,
            value: nextValue ?? "",
        });

    };



    // This checks whether the store alias is available.
    const handleCheckAlias = (alias) => {

        // Check the alias through the API
        checkConnectionAlias(token, alias)

        .then((response) => {

            if (!response.success) {

                // Check if the input value is the same as their initial form values - we don't want to display a message
                if (alias === initialFormValues.alias) {

                    // If it is clear the message
                    setAliasExists(null);

                } else {

                    // Otherwise set the message
                    setAliasExists(response.error_message);

                }

            } else {

                // Set the message
                setAliasExists("This URL alias is available.");

            }

        })

        .catch((error) => {

            console.error("Error checking alias:", error);

        });
    };



    // This copies text with a fallback for older browsers.
    const copyWithFallback = (textToCopy) => {

        // Create a temporary textarea element
        const textArea = document.createElement("textarea");

        // Set the textarea value to the text we want to copy
        textArea.value = textToCopy;

        // Prevent mobile keyboard opening where possible
        textArea.setAttribute("readonly", "");

        // Move the textarea off screen
        textArea.style.position = "absolute";
        textArea.style.left = "-9999px";

        // Add the textarea to the page
        document.body.appendChild(textArea);

        // Select the textarea content
        textArea.select();

        // Ensure full selection on iOS style browsers
        textArea.setSelectionRange(0, textArea.value.length);

        try {

            // Attempt to copy the selected text
            const successful = document.execCommand("copy");

            // Remove the temporary textarea
            document.body.removeChild(textArea);

            // Return whether copy succeeded
            return successful;

        } catch (error) {

            // Remove the temporary textarea even if copy fails
            document.body.removeChild(textArea);

            // Return false when copy fails
            return false;

        }

    };



    // This copies the store URL and shows short feedback.
    const handleCopyStoreLink = async (alias) => {

        // Clear any previous error
        setCopyError("");

        // Stop if there is no alias
        if (!alias) {

            // Exit early
            return;

        }

        // Build the full store URL
        const fullUrl = `https://strich.store/${alias}`;

        try {

            // Use the modern clipboard API if available
            if (navigator.clipboard && window.isSecureContext) {

                // Copy the full URL to the clipboard
                await navigator.clipboard.writeText(fullUrl);

            } else {

                // Use the fallback method for older or insecure environments
                const fallbackSuccess = copyWithFallback(fullUrl);

                // Throw an error if fallback copy failed
                if (!fallbackSuccess) {

                    throw new Error("Clipboard copy failed");

                }

            }

            // Show copied feedback
            setCopied(true);

            // Remove copied feedback after 2 seconds
            setTimeout(() => {

                // Reset copied state
                setCopied(false);

            }, 1500);

        } catch (error) {

            // Log the error for debugging
            console.error("Failed to copy alias:", error);

            // Show a user friendly error
            setCopyError("Unable to copy the store URL.");

        }

    };




    
    const isStrichConnection = userData?.email === "customizr@strichsuite.com";



    const storeTabs = [
        { label: 'Details' },
        ...(connectionDetails?.conntype === "STRICH" ? [{ label: "Styling" }] : []),
        ...(connectionDetails?.conntype === "STRICH" ? [{ label: "Pages" }] : []),
        ...(connectionDetails?.conntype === "STRICH" && appConfig.showEmailTemplates ? [{ label: "Emails" }] : []),
        ...(isStrichConnection ? [{ label: "STRICH Settings" }] : []),
    ];



    // Render tab contents 
    const renderConnectionTabs = () => {

        switch (storeTabs[activeTab]?.label) {

            case "Details":

                // Details

                return (

                    <div className="popup_grid">

                        {/* This is the store details */}
                        <div className="card">

                            <div className="card__header connection-details-card__header">

                                <h3>Store Details</h3>

                                <span className="hintText">
                                    Set your basic store details here
                                </span>

                            </div>

                            <div className="card__content">

                                <br/>

                                <div className="horizontal-align-content-center">

                                    <label className="store-details_label">Store Name</label>

                                    <InfoButton
                                        shortLabel="Store Name"
                                        description="This will be the name on your store's homepage."
                                    />
                                
                                </div>

                                <input
                                    name="connectionname"
                                    type="text"
                                    value={formValues?.connectionname || ""}
                                    onChange={handleChange}
                                    
                                />
                                {fieldErrors.connectionname && <p style={{ color: "red" }}>{fieldErrors.connectionname}</p>}

                                <br />
                                <br />
                                <br />

                                <div className="horizontal-align-content-center">

                                    <label className="store-details_label">Store URL</label>

                                    <InfoButton
                                        shortLabel="Store URL"
                                        description="This becomes your store's web address and is the link you can share with customers."
                                    />
                                
                                </div>

                                <div className="store-details_alias">

                                    <div className="store-details_alias-url">https://www.strich.store/</div>

                                    <input
                                        name="alias"
                                        type="text"
                                        value={formValues?.alias ?? ""}
                                        maxLength="50"
                                        style={{
                                            width: "100%",
                                            minWidth: "110px",
                                        }}
                                        onChange={(event) => {

                                            // Convert uppercase characters into the canonical lowercase alias
                                            const normalisedAlias =
                                                event.target.value.toLowerCase();

                                            // Store the normalised alias in the form
                                            handleChange({
                                                target: {
                                                    name: event.target.name,
                                                    value: normalisedAlias,
                                                },
                                            });

                                            // Check the normalised alias through the API
                                            handleCheckAlias(normalisedAlias);

                                        }}
                                    />

                                    <div className="store-details_alias-action">

                                        {fieldErrors.alias ?

                                            <span
                                                className="store-details_alias-message store-details_alias-message-error"
                                                title={fieldErrors.alias}
                                            >

                                                ✘ Required

                                            </span>

                                            :

                                            aliasExists ?

                                                <span
                                                    className="store-details_alias-message"
                                                    title={aliasExists}
                                                    style={{color: aliasExists === "This URL alias is available." ? "green" : "red"}}
                                                >

                                                    {aliasExists === "This URL alias is available."
                                                        ? "✔ Available"
                                                        : aliasExists === "Alias must be at least 4 characters long"
                                                            ? "✘ Min. 4 characters"
                                                            : aliasExists === "Alias cannot be more than 50 characters long"
                                                                ? "✘ Max. 50 characters"
                                                                : aliasExists === "Alias already in use"
                                                                    ? "✘ Already in use"
                                                                    : "✘ Invalid alias"}

                                                </span>

                                            :

                                            !addingNewConnection && !aliasHasBeenEdited ?

                                                <CreateButton
                                                    type="button"
                                                    title={isMobile && copied ? "✅" 
                                                        : isMobile && !copied ? "📋"
                                                        : !isMobile && copied ? "✅ Copied"
                                                        : "📋 Copy Link" }
                                                    size="xs"
                                                    plain={true}
                                                    onClick={() => handleCopyStoreLink(formValues.alias)}
                                                />

                                                :

                                                copyError &&

                                                    <span
                                                        className="store-details_alias-message store-details_alias-message-error"
                                                        title={copyError}
                                                    >

                                                        ✘ Copy failed

                                                    </span>

                                        }

                                    </div>
                                    
                                </div>

                                {!addingNewConnection &&

                                    <>

                                        <br/>
                                        <br/>

                                        <div className="horizontal-align-content-center flex-justify-left-right">

                                            <div className="horizontal-align-content-center">

                                                <label className="store-details_label">Auto Add to Marketplace</label>

                                                <InfoButton
                                                    shortLabel="Auto Add to Marketplace"
                                                    description="Automatically add new designs and products from this store to the Strich marketplace."
                                                />

                                            </div>

                                            <ToggleSwitch
                                                name="store_marketplace_autoadd"
                                                size="s"
                                                value={
                                                    String(
                                                        formValues?.settings?.find(
                                                            (setting) => setting.code === "store_marketplace_autoadd"
                                                        )?.value ?? "Y"
                                                    ).toUpperCase() === "Y"
                                                }
                                                onChange={(event) => {

                                                    // Save the marketplace auto-add setting as the API's Y/N value
                                                    handleSettingValueChange(
                                                        "Store",
                                                        "store_marketplace_autoadd",
                                                        "Marketplace Auto Add",
                                                        "Automatically add products to the marketplace?"
                                                    )(event.target.value ? "Y" : "N");

                                                }}
                                            />

                                        </div>

                                    </>

                                }

                                <br/>
                                <br/>
                                
                                {addingNewConnection ? 
                                                                
                                    <>

                                        {/* This is the Country dropdown list - made from core coutries where account is set to "Y" */}
                                        <label className="store-details_label">Store Country: </label>
                                        <select
                                            name="country" 
                                            value={formValues.country || userData.country}
                                            onChange={handleChange}
                                            style={{width: "auto"}}
                                        >
                                            {countries.filter((country) => country.account === "Y").map((country) => (

                                                <option key={country.code} value={country.code}>
                                                    {country.name}
                                                </option>
                                                
                                            ))}

                                        </select>

                                    </>

                                    :

                                    <>

                                        <div className="horizontal-align-content-center">

                                            <label className="store-details_label">Store Short Description</label>

                                            <InfoButton
                                                shortLabel="Store Short Description"
                                                description="Short text shown in the website footer above the social links."
                                            />
                                        
                                        </div>

                                        <input
                                            name="store_short_description"
                                            type="text"
                                            maxLength={250}
                                            value={formValues?.settings?.find((setting) => setting.code === "store_short_description")?.value || ""}
                                            onChange={(event) => {

                                                // Reuse existing settings handler with a synthetic event
                                                handleSettingsChange("Store Short Description", "store_short_description")({
                                                    target: {
                                                        type: "text",
                                                        value: event.target.value,
                                                    },
                                                });

                                            }}
                                        />

                                        <br/>
                                        <br/>

                                        <div className="horizontal-align-content-center">

                                            <label className="store-details_label">Store Email</label>

                                            <InfoButton
                                                shortLabel="Store Email"
                                                description="Contact email address shown on the store and emails."
                                            />
                                        
                                        </div>    

                                        <input
                                            name="store_email"
                                            type="text"
                                            value={formValues?.settings?.find((setting) => setting.code === "store_email")?.value || ""}
                                            onChange={(event) => {

                                                // Reuse existing settings handler with a synthetic event
                                                handleSettingsChange("Store Email", "store_email")({
                                                    target: {
                                                        type: "text",
                                                        value: event.target.value,
                                                    },
                                                });

                                            }}
                                        />
                                        {fieldErrors.store_email && <p style={{ color: "red" }}>{fieldErrors.store_email}</p>}

                                        <br/>
                                        <br/>

                                        <div className="horizontal-align-content-center">

                                            <label className="store-details_label">Store Country</label>

                                            <InfoButton
                                                shortLabel="Store Country"
                                                description="This is the country your store is based in."
                                            />
                                        
                                        </div>  
                                           
                                        <input
                                            name="store_country"
                                            type="text"
                                            value={connectionDetails.country}
                                            disabled
                                        />


                                        <br/>
                                        <br/>

                                    </>

                                }

                                {formError && <p style={{ color: "red" }}>{formError}</p>}

                            </div>

                        </div>

                        {/* Keep shipping and currencies together within one responsive grid column */}
                        <div className="popup_coloumn">

                            {/* This is the Store Shipping Methods card */}
                            <RenderConnectionShippingMethods 
                                connectionDetails={connectionDetails}
                                addingNewConnection={addingNewConnection} 
                                formValues={formValues}
                                handleChange={handleChange}
                            />


                            {/* This is the Store Currencies card */}
                            <div className="card">

                                <div className="card__header connection-details-card__header">

                                    <h3>Currencies</h3>

                                    <span className="hintText">

                                        {addingNewConnection ? 
                                        
                                            "Set the default currency for this store"

                                            :

                                            "Choose the currencies you want to accept for orders through this store"

                                        }

                                    </span>

                                </div>

                                <div className="card__content">

                                    {addingNewConnection ?

                                        <>

                                            {/* This is the store's main currency */}
                                            <div className="horizontal-align-content-center">

                                                <label className="store-details_label">Store Currency</label>

                                                <InfoButton
                                                    shortLabel="Store Currency"
                                                    description="This is the default currency that the store will take payments in."
                                                />
                                            
                                            </div> 

                                            <br/>

                                            <select
                                                name="currency" 
                                                value={formValues.currency || userData.currency}
                                                onChange={handleChange}
                                                style={{width: "auto"}}
                                            >

                                                {["GBP", "USD", "EUR"].map((currencyCode) => (

                                                    <option key={currencyCode} value={currencyCode}>
                                                        {currencyCode}
                                                    </option>

                                                ))}

                                            </select>

                                        </>

                                        :

                                        <>

                                            <div className="horizontal-align-content-center">

                                                <label className="store-details_label">Store Currency</label>

                                                <InfoButton
                                                    shortLabel="Store Currency"
                                                    description="This is the default currency that the store will take payments in."
                                                />
                                            
                                            </div> 

                                            <input
                                                name="store_currency"
                                                type="text"
                                                value={connectionDetails.currency}
                                                disabled
                                            />

                                            <br/>
                                            <br/>

                                            <div className="horizontal-align-content-center">

                                                <label className="store-details_label">Accepted Currencies</label>

                                                <InfoButton
                                                    shortLabel="Store Currency"
                                                    description="Earnings may differ based on currency conversion. Click 'Advanced' pricing to view full breakdowns in a POD design product."
                                                />
                                            
                                            </div> 

                                            <br/>

                                            {/* Allow user to select the currencies they want available on their store */}
                                            <div className="selectable__chips">

                                                {["GBP", "USD", "EUR"].map((currencyCode) => {

                                                    // Get the current settings array safely
                                                    const safeSettings = Array.isArray(formValues?.settings) ? formValues.settings : [];

                                                    // Find the currencies setting safely
                                                    const currenciesSetting = safeSettings.find((setting) => setting.type === "Pricing" && setting.code === "currencies");

                                                    // Get the selected currencies safely
                                                    const selectedCurrencies = new Set(
                                                        (currenciesSetting?.value || "")
                                                            .split(",")
                                                            .map((currency) => currency.trim())
                                                            .filter(Boolean)
                                                    );

                                                    // Check if this currency is currently selected
                                                    const isSelected = selectedCurrencies.has(currencyCode);

                                                    // Handle toggling the currency on or off
                                                    const toggleCurrencyValue = () => {

                                                        // Create a fresh Set so the render value is not mutated directly
                                                        const nextSelectedCurrencies = new Set(selectedCurrencies);

                                                        // Remove or add the selected currency
                                                        if (isSelected) {

                                                            nextSelectedCurrencies.delete(currencyCode);

                                                        } else {

                                                            nextSelectedCurrencies.add(currencyCode);

                                                        }

                                                        // Prevent the user from removing every currency
                                                        if (nextSelectedCurrencies.size === 0) {

                                                            toastError({
                                                                title: "Error",
                                                                message: "At least one currency must be selected.",
                                                                autoCloseMs: 6000,
                                                            });

                                                            return;

                                                        }

                                                        // Convert the selected currencies back to the stored comma-separated value
                                                        const nextValue = Array.from(nextSelectedCurrencies).join(",");

                                                        // Update the existing currencies setting
                                                        handleSettingsChange("Pricing", "currencies")({
                                                            target: {
                                                                type: "text",
                                                                value: nextValue,
                                                            },
                                                        });

                                                    };

                                                    return (

                                                        // This is one selectable currency
                                                        <button
                                                            key={currencyCode}
                                                            type="button"
                                                            className={`selectable__chip ${isSelected ? "selectable__chip--selected" : ""}`}
                                                            onClick={() => toggleCurrencyValue()}
                                                            title={isSelected ? `Remove ${currencyCode}` : `Allow ${currencyCode}`}
                                                        >

                                                            <span className="selectable__chipTick">{isSelected ? "✓" : "+"}</span>

                                                            <span className="selectable__chipLabel">{currencyCode}</span>

                                                        </button>

                                                    );

                                                })}

                                            </div>

                                        </>

                                    }

                                </div>

                            </div>

                        </div>


                        {/* Render Socials Settings */}
                        <RenderConnectionSocials 
                            connectionDetails={connectionDetails} 
                            formValues={formValues}
                            addingNewConnection={addingNewConnection} 
                            handleSettingsChange={handleSettingsChange} 
                        />


                    </div>

                );

            case "Styling":

                // Styling

                return (

                   <div>

                        <InlineStorePreviewSettings
                            isOpen={isOpen}
                            addingNewConnection={addingNewConnection}
                            connectionType={connectionDetails?.conntype || initialFormValues?.conntype || ""}
                            savedSettings={connectionDetails?.settings || []}
                            formSettings={formValues.settings || []}
                            previewAlias="portalsettings"
                            availablePodTemplates={availablePodTemplates}
                            handleSettingsChange={handleSettingsChange}
                            handleSettingValueChange={handleSettingValueChange}
                        />

                   </div>

                );

            case "Pages":

                // Pages

                return (
                   
                    <div>

                        <RenderConnectionPages 
                            connectionDetails={connectionDetails}
                            token={token}
                            formValues={formValues}
                            setFormValues={setFormValues}
                        />

                    </div>

                );


            case "Emails":

                // Email Templates

                return (
                   
                    <div>

                        <RenderConnectionEmailTemplates />

                    </div>

                );
            
            case "STRICH Settings":

                // STRICH Settings - New Settings Input ( STRICH ACCOUNT ONLY )

                if (!isStrichConnection) {

                    return null;

                }

                return (
                   
                    <div>

                        <RenderConnectionSettings 
                            connectionDetails={connectionDetails} 
                            addingNewConnection={addingNewConnection} 
                            formValues={formValues} 
                            setFormValues={setFormValues}
                            token={token}
                            fetchConnections={fetchConnections}
                        />

                    </div>

                );

                        
            default:

                return null;

        }

    };


    // If isOpen is false return early (don't show the pop up)
    if (!isOpen) return null;



    return (

        <div className="popup-overlay">

            <div className="popup-container popup-container--large">

                {/* This is the modal header */}
                <div className="popup__header">

                    {/* This is the title area */}
                    <div className="popup__headerLeft">

                        {/* This is the parent title */}
                        <h2 className="popup__title">

                            {addingNewConnection ? "Create a new store" : connectionDetails?.connectionname }

                        </h2>

                        {/* This is the parent subtitle */}
                        <p className="popup__subtitle">

                        </p>

                    </div>

                    {/* This is the header actions */}
                    <div className="popup__headerRight">

                        <OpenStoreButton alias={initialFormValues?.alias} buttonTitle={"View Store"} disabled={!formValues?.alias || loading} />

                        {/* This is the close button */}
                        <button type="button" className="popup__close" onClick={() => handleCloseButton("You have unsaved changes. Are you sure you want to close?")} title="Close">
                            <CloseIcon />
                        </button>

                    </div>

                </div>

                {loadingConnection ? 

                    <div>
                                                                
                        <div style={{textAlign: "center"}}>
                            <br/>
                            <LoadingSpinner />
                            <br/>
                            Loading...
                        </div>
                        
                    </div>

                    :
                
                    <>

                        <div
                            ref={popupContentRef}
                            className="popup-content"
                        >
                        
                            <form onSubmit={handleSubmit}>

                                

                                <div>
                                  
                                    {!addingNewConnection && 

                                    // Tab headers 
                                    <div className="tabs-header tabs-header--connections">

                                        {/* Map through the tabs to get the labels to make the headers */}
                                        {storeTabs.map((tab, index) => (

                                            <div
                                                key={index}
                                                className={`tabs-header__tab tabs-header__tab--connections ${activeTab === index ? 'tabs-header__tab--active' : ''}`}

                                                // Set the tab indix to this one on click
                                                onClick={() => setActiveTab(index)}
                                            >
                                            
                                                {tab.label} 
                                            
                                            </div>

                                        ))}

                                        {/* Animated underline tab indicator */}
                                        <div className="tabs-header__indicator" style={{ transform: `translateX(${activeTab * 100}%)`, width: `calc(100% / ${storeTabs.length})` }} />

                                    </div>

                                    }

                                    {/* Tab content */}
                                    {/* Scrollable content area */}
                                    <div className="tabs-scroll-container">

                                        <div className="tabs-content">

                                            {renderConnectionTabs()}

                                        </div>

                                    </div>
 
                                </div>

                            </form>
        
                        </div>

                        <div className="tabs-footer tabs-footer--no-background horizontal-align-content-center flex-justify-left-right">

                            <div>

                                {/* Only show delete button we're not adding a new one */}
                                {!addingNewConnection &&
                                
                                    <CreateButton title="Delete Store" isDelete={true} tooltip="Delete Store" size="s" icon="/images/icon_bin.png" onClick={() => handleDeleteConnection(connectionDetails.id)} disabled={loading} />

                                }

                            </div>

                            <div className="horizontal-align-content-center">

                                {/* Only show the save action when there is something to save. */}
                                {(addingNewConnection || hasUnsavedChanges || loading) &&

                                    <CreateButton
                                        title={
                                            loading
                                                ? addingNewConnection ? "Creating..." : "Saving..."
                                                : addingNewConnection ? "Create Store" : "Save changes"
                                        }
                                        size="m"
                                        type="button"
                                        onClick={handleSubmit}
                                        disabled={loading}
                                    />

                                }

                                {/* Close the popup, warning first when unsaved changes remain. */}
                                <CreateButton 
                                    title="Close"
                                    size="m" 
                                    invert 
                                    type="button" 
                                    onClick={() => {handleCloseButton("You have unsaved changes. Are you sure you want to close?")}} 
                                    disabled={loading}
                                />

                            </div>

                        </div>

                    </>

                }

            </div>

        </div>

    );

 };

export default ConnectionDetailsPopUp;
